import { hydrateRoot } from 'react-dom/client';
import { createI18nManager, createResources, Instance } from '@funtech-int/rival';
import {
    createLocalStorage,
    createMemoryStorage,
    createPopupsManager,
    createSessionStorage,
    createStorageManager,
} from '@funtech-int/web-toolkit';
import {
    createEvgen,
    createFromBlock,
    createRemoteConfig,
    createRequestIdManager,
    createFeatureFlags,
    createGtmService,
    createQueryParamsStore,
    createVitalsManager,
    createPayments,
    createDcbManager,
} from '@funtech-int/yango-shared';
import { installAbortSignalPolyfill } from 'abort-signal-polyfill';
import { nanoid } from 'nanoid';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { getGeoReplacementHeaders } from '@funtech-int/i18n-geo-widget/sdk';
import { fromBrowser } from '@funtech-int/i18n-geo-widget/sdk/fromBrowser';

import { runtimeConfig } from '@/shared/lib/runtimeConfig';
import { getPaymentWidgetNode } from '@/shared/lib/paymentManager';

import { createLogger } from './shared/lib/logger/client';
import { getDeviceUuid } from './shared/lib/deviceUuid';
import { createInMemoryCache } from './shared/lib/inMemoryCache';
import { createProfileManager } from './shared/lib/profileManager';
import { createBackendClient } from './shared/lib/backendClient';
import { createGraphqlClient } from './shared/lib/graphqlClient';
import { clientConfig } from './config/client';
import { appFactory } from './app';
import { createRumClientManager } from './app/lib/rum/client';
import { createVitalsReporter } from './app/lib/vitalsReporter';
import { webVitalsMetric } from './app/lib/metrics';
import { offersRequester, getPlusPaymentEnvironment } from './shared/lib/payments';
import { createAuthManager } from './app/lib/authManager';
import { MetrikaProvider } from './legacy/metrika/metrika-provider';
import { fetcherLoggingMiddleware } from './shared/lib/fetcherMiddlewares';

declare const window: Window & {
    __APP__: any;
};

installAbortSignalPolyfill();

const {
    environment,
    graphQlPublicApi,
    saftGatewayApi,
    serviceId,
    i18n,
    productServiceName,
    appVersion,
    trackingsHost,
    yandexMetrika,
    shouldUseConsoleLogger,
    paymentToolsConfig,
} = clientConfig;

/**
 * Фабрика клиентских зависимостей по аналогии с https://a.yandex-team.ru/arcadia/kinopoisk/frontend/kp/services/yango-movies-touch-www/src/client.ts
 */
function clientFactory() {
    const provider = new WebTracerProvider();

    provider.register({
        propagator: new W3CTraceContextPropagator(),
    });

    const logger = createLogger({ withConsoleTransport: shouldUseConsoleLogger });
    const lang = runtimeConfig.get('localeState').locale;

    const remoteConfig = createRemoteConfig();

    const featureFlags = createFeatureFlags();

    const requestIdManager = createRequestIdManager();

    const resourcesManager = createResources();

    const defaultHeaders: Record<string, string> = {
        ...getGeoReplacementHeaders(fromBrowser),
    };

    const graphqlClient = createGraphqlClient({
        graphqlBaseUrl: graphQlPublicApi,
        requestIdManager,
        logger,
        lang,
        serviceId,
        timeout: 60000,
        defaultHeaders,
        middlewares: [fetcherLoggingMiddleware(logger, 'browser-graphql-client')],
    });

    const profileManager = createProfileManager({
        graphqlClient,
    });

    const authManager = createAuthManager({
        lang,
    });

    const backendClient = createBackendClient({
        saftGatewayApi,
        requestIdManager,
        logger,
        lang,
    });

    const i18nManager = createI18nManager({
        defaultLocale: i18n.defaultLocale,
        locales: i18n.locales,
    });

    i18nManager.switch(lang);

    const storage = createStorageManager({
        local: createLocalStorage,
        session: createSessionStorage,
        memory: createMemoryStorage,
    });

    getDeviceUuid(storage);

    const trackedQueryParamsStore = createQueryParamsStore({
        storage: storage.session,
    });

    trackedQueryParamsStore.initialize();

    const fromBlock = createFromBlock(storage);

    const appData = window.__APP__ ?? window.__NEXT_DATA__.props.appManager;

    const featureFlagsData = appData['Symbol(featureFlags)'].flags;
    const testIdsData = appData['Symbol(featureFlags)'].testIds;

    const payments = createPayments({
        lang,
        authManager: {
            userProfile: {
                get puid() {
                    return profileManager.userProfile?.puid;
                },
            },
        },
        serviceParams: {
            id: String(serviceId),
            name: paymentToolsConfig.serviceName,
            channel: paymentToolsConfig.serviceChannelName,
            widgetServiceName: paymentToolsConfig.widgetServiceName,
            brand: paymentToolsConfig.brand,
            theme: paymentToolsConfig.theme,
            counterId: yandexMetrika.main,
        },
        sessionParams: {
            authMethod: 'default' as const,
            serviceSessionId: nanoid(),
            environment: getPlusPaymentEnvironment(environment),
        },
        expFlags: featureFlagsData,
        expTestIds: testIdsData,
        requester: offersRequester,
        getWidgetTargetNode: getPaymentWidgetNode,
        logger,
    });

    const dcbManager =
        clientConfig.appBrand === 'yango'
            ? createDcbManager({
                  brand: paymentToolsConfig.brand,
                  environment: getPlusPaymentEnvironment(environment),
                  requester: offersRequester,
                  getIsAuthorized: () => authManager.isAuthorized,
                  getHasSubscription: () => profileManager.hasSubscription.get(),
                  logger,
              })
            : undefined;

    const evgen = createEvgen({
        featureFlags,
        // приведение к формату использования в тачах https://a.yandex-team.ru/arcadia/kinopoisk/frontend/kp/services/yango-movies-touch-www/src/client.ts
        authManager: {
            isAuthorized: appData['Symbol(authManager)'].isAuthorized,
            userProfile: {
                get puid() {
                    return profileManager.userProfile?.puid;
                },
                get billingFeatureNames() {
                    return profileManager.billingFeatureNames.get();
                },
            },
        },
        trackedQueryParamsStore,
        locale: i18nManager.locale.get(),
        runtimeConfig: runtimeConfig.get(),
        serviceName: productServiceName,
        logger,
        sessionIds: fromBlock.sessionIds,
        appVersion,
        yandexuid: appData['Symbol(authManager)'].yandexuid ?? '',
        trackingsHost,
        metrikaProvider: new MetrikaProvider({
            initialParams: { id: yandexMetrika.main, yaGdprLang: lang },
            scriptSrc: yandexMetrika.scriptSrc,
        }),
    });

    const popups = createPopupsManager();

    const gtmService = createGtmService({ logger, trackedQueryParamsStore });

    const inMemoryCache = createInMemoryCache();

    const rumManager = createRumClientManager();

    const vitalsManager = createVitalsManager({
        globals: [webVitalsMetric],
        onReport: createVitalsReporter({
            rum: rumManager,
            verbose: clientConfig.environment !== 'production',
        }).onReport,
    });

    const dependencies: Instance<any>[] = [
        authManager,
        backendClient,
        evgen,
        featureFlags,
        fromBlock,
        graphqlClient,
        gtmService,
        i18nManager,
        inMemoryCache,
        trackedQueryParamsStore,
        logger,
        popups,
        profileManager,
        remoteConfig,
        requestIdManager,
        resourcesManager,
        storage,
        rumManager,
        vitalsManager,
        payments,
        ...(dcbManager ? [dcbManager] : []),
    ];

    return { dependencies, featureFlagsData };
}

const rootElement = document.getElementById('app-container');

if (!rootElement) {
    throw new Error("Client: can't find root dom element");
}

const app = appFactory(clientFactory());

hydrateRoot(rootElement, app());
